import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

const Message = ({ content, type, options, onOptionClick, isStreaming, onClick, subtopics, onSubtopicSelect }) => {
  const [displayedContent, setDisplayedContent] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [selectedSubtopics, setSelectedSubtopics] = useState([]);

  useEffect(() => {
    if (isStreaming && !isComplete) {
      let index = 0;
      const interval = setInterval(() => {
        if (index < content.length) {
          setDisplayedContent(prev => prev + content[index]);
          index++;
        } else {
          clearInterval(interval);
          setIsComplete(true);
          if (onClick) onClick();
        }
      }, 20);

      return () => clearInterval(interval);
    }
  }, [content, isStreaming, isComplete, onClick]);

  const handleSubtopicClick = (topicName, subtopicName, isOpen) => {
    if (!isOpen) return; // Prevent selection if topic is locked

    setSelectedSubtopics([`${topicName}-${subtopicName}`]);
  };

  const handleContinue = () => {
    if (selectedSubtopics.length > 0) {
      onSubtopicSelect(selectedSubtopics);
    }
  };

  return (
    <div className={`flex ${type === 'ai' ? 'justify-start' : 'justify-end'} mb-4`}>
      <div
        className={`max-w-[80%] ${type === 'ai' ? 'bg-[#252525]' : 'bg-[#5c8dff]'} rounded-lg p-4`}
        onClick={() => {
          if (!isComplete) {
            setDisplayedContent(content);
            setIsComplete(true);
            if (onClick) onClick();
          }
        }}
      >
        <div className="text-white">
          {isStreaming ? displayedContent : content}
        </div>

        {subtopics && isComplete && (
          <div className="mt-4 space-y-4">
            {subtopics.map((topic, index) => (
              <div key={index} className="bg-[#303030] rounded-lg p-3">
                <h3 className="text-white font-medium mb-2">{topic.name}</h3>
                <div className="space-y-2">
                  {topic.subTopics.map((subtopic, subIndex) => {
                    const isSelected = selectedSubtopics.includes(`${topic.name}-${subtopic.name}`);
                    return (
                      <button
                        key={subIndex}
                        onClick={() => handleSubtopicClick(topic.name, subtopic.name, subtopic.open)}
                        disabled={!subtopic.open}
                        className={`w-full text-left p-2 rounded flex justify-between items-center ${isSelected
                            ? 'bg-[#5c8dff] text-white'
                            : subtopic.open
                              ? 'bg-[#404040] text-gray-200 hover:bg-[#454545]'
                              : 'bg-[#303030] text-gray-400 cursor-not-allowed'
                          } transition-colors duration-200 text-sm`}
                      >
                        <span>{subtopic.name}</span>
                        {!subtopic.open ? (
                          <span>🔒</span>
                        ) : isSelected ? (
                          <span>✓</span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <button
              onClick={handleContinue}
              className={`w-full p-3 rounded-lg ${selectedSubtopics.length > 0
                  ? 'bg-[#5c8dff] hover:bg-[#4a71cc]'
                  : 'bg-[#404040] cursor-not-allowed'
                } text-white transition-colors duration-200 mt-4`}
            >
              Continue with selected topics
            </button>
          </div>
        )}

        {options && isComplete && !subtopics && (
          <div className="mt-4 space-y-2">
            {options.map((option, index) => (
              <button
                key={index}
                onClick={() => onOptionClick(option)}
                className="w-full text-left p-3 bg-[#303030] hover:bg-[#404040] rounded-lg text-white transition-colors duration-200"
              >
                {option}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const AIChatModal = ({ isOpen, onClose, onComplete, esubjects: initialEsubjects, msubjects: initialMsubjects }) => {
  // Deep copy the subjects and add open property
  const [esubjects] = useState(() =>
    initialEsubjects.map(topic => ({
      ...topic,
      subTopics: topic.subTopics.map(subtopic => ({
        ...subtopic,
        open: false  // Default all to false
      }))
    }))
  );

  const [msubjects] = useState(() =>
    initialMsubjects.map(topic => ({
      ...topic,
      subTopics: topic.subTopics.map(subtopic => ({
        ...subtopic,
        open: topic.name === 'Advanced Math' &&
          (subtopic.name === 'Nonlinear functions' || subtopic.name === 'Equivalent expressions')
      }))
    }))
  );
  const [messages, setMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [learningType, setLearningType] = useState('');
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [isStreaming, setIsStreaming] = useState(true);
  const chatContainerRef = useRef(null);

  const steps = [
    {
      message: "Hi! I'm your AI learning assistant. I'll help you create a personalized learning path. First, which subject would you like to focus on?",
      options: ['English', 'Math']
    },
    {
      message: (subject) => `Great! Let's personalize your ${subject || ''} learning experience. Please select the topics you'd like to focus on:`,
      subtopics: true
    },
    {
      message: "How would you prefer to learn these topics?",
      options: ['I learn best by reading and studying', 'I learn best by solving problems']
    },
    {
      message: () => {
        const topicCount = selectedTopics?.length || 0;
        const topicText = topicCount === 1 ? 'topic' : 'topics';
        return `Perfect! I've created a personalized learning path with ${topicCount} ${topicText}. Are you ready to take a placement test to determine your starting point?`;
      },
      options: ['Start Placement Test', 'I need more time']
    }
  ];

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addMessage('ai', steps[0].message, steps[0].options);
    }
  }, [isOpen]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const addMessage = (type, content, options = null, subtopics = null) => {
    setMessages(prev => [...prev, { type, content, options, subtopics }]);
    setIsStreaming(true);
  };

  const handleSubtopicSelect = (selectedSubtopics) => {
    setSelectedTopics(selectedSubtopics);
    addMessage('user', `Selected ${selectedSubtopics.length} topics`);

    setTimeout(() => {
      addMessage('ai', steps[2].message, steps[2].options);
      setCurrentStep(2);
    }, 500);
  };

  const resetModal = () => {
    setMessages([]);
    setCurrentStep(0);
    setSelectedSubject('');
    setLearningType('');
    setSelectedTopics([]);
    setIsStreaming(true);
  };

  const handleClose = () => {
    onClose();
    setTimeout(resetModal, 300); // Reset after animation
  };

  const handleOptionClick = (option) => {
    addMessage('user', option);

    if (currentStep === 0) {
      setSelectedSubject(option);
      const subjects = option === 'English' ? esubjects : msubjects;
      setTimeout(() => {
        addMessage('ai', steps[1].message(option), null, subjects);
        setCurrentStep(1);
      }, 500);
    } else if (currentStep === 2) {
      setLearningType(option);
      setTimeout(() => {
        addMessage('ai', steps[3].message(), steps[3].options);
        setCurrentStep(3);
      }, 500);
    } else if (currentStep === 3) {
      if (option === 'Start Placement Test') {

      } else if (option === 'I need more time') {
        setTimeout(() => {
          handleClose();
        }, 500);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-[#1a1a1a] rounded-xl w-full max-w-2xl h-[600px] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-[#2B2B2B]">
          <h1 className="text-xl font-bold text-white">AI Course Setup</h1>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white transition-colors duration-200"
          >
            <X size={24} />
          </button>
        </div>

        <div
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto p-4 space-y-4"
        >
          {messages.map((message, index) => (
            <Message
              key={index}
              {...message}
              isStreaming={isStreaming && index === messages.length - 1}
              onOptionClick={handleOptionClick}
              onSubtopicSelect={handleSubtopicSelect}
              onClick={() => setIsStreaming(false)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AIChatModal;